import { stakewiseVaultAbi } from "../stakewise/stakewise-interfaces.js";
import { _verifyCore } from "./verification.js";
import { _verifyArgs } from "./verification-helpers.js";
/**
 * @internal Verify Stakewise stake/deposit transaction.
 *
 * Verifies a deposit transaction to a Stakewise vault.
 *
 * **Verification Steps:**
 * 1. Validates chain ID matches expected value (if provided)
 * 2. Verifies target address matches vault address
 * 3. Checks target address against allowlist (if provided)
 * 4. Verifies function name is "deposit"
 * 5. Verifies transaction value matches requested assets amount
 * 6. Optionally verifies function arguments match expected values
 *
 * @param request - Stake request containing assets amount and user address
 * @param tx - Transaction to verify
 * @param options - Verification options including expected chain ID and vault address
 * @param logger - Logger instance for debug output
 * @returns Verification result with success status, message, and details
 */
export function _stakewiseVerifyStakeUnsignedTx(request, tx, options, logger) {
    logger.debug("[eth] stakewiseVerifyStakeUnsignedTx", { userAddress: request.userAddress });
    const expectedTo = request.vaultAddress ?? options.defaultVaultAddress;
    const coreOptions = {
        abi: stakewiseVaultAbi,
        functionName: "deposit",
        expectedChainId: options.expectedChainId,
        allowedToAddresses: options.allowedToAddresses,
        deriveExpectedArgs: options.deriveExpectedArgs,
    };
    const result = _verifyCore(tx, coreOptions, logger, { chainId: request.chainId, expectedTo });
    if (!result.ok)
        return result;
    if (tx.value !== request.assets) {
        return {
            ...result,
            ok: false,
            message: `Value mismatch: expected ${request.assets}, got ${tx.value}`,
            details: {
                ...result.details,
                expectedValue: request.assets.toString(),
                actualValue: tx.value.toString(),
            },
        };
    }
    if (options.deriveExpectedArgs && result.decoded) {
        const expectedArgs = options.deriveExpectedArgs(request);
        const argsResult = _verifyArgs(result.decoded, expectedArgs, logger);
        if (!argsResult.ok) {
            return { ...result, ok: false, message: argsResult.message ?? "Args verification failed" };
        }
    }
    return result;
}
//# sourceMappingURL=data:application/json;base64,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